import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  ArrowLeft, Gift, Coins, Users, TrendingUp, Wallet,
  CheckCircle, Clock, Award, Star, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REWARD_TYPES = [
  { 
    id: 'subscription_rewards', 
    name: 'Subscription Rewards', 
    icon: Coins,
    color: 'amber',
    description: 'Bonus credits for subscription renewals'
  },
  { 
    id: 'staking_rewards', 
    name: 'Staking Rewards', 
    description: 'Rewards for staking BETH tokens',
    icon: TrendingUp,
    color: 'blue'
  },
  { 
    id: 'referral_rewards', 
    name: 'Referral Rewards', 
    description: 'Earn by referring new users',
    icon: Users,
    color: 'green'
  },
  { 
    id: 'platform_incentives', 
    name: 'Platform Incentives', 
    description: 'Bonus BETH for platform activity',
    icon: Star,
    color: 'yellow'
  }
];

const ClaimRewards = () => {
  const { token, user } = useAuth();
  const { account } = useWeb3();
  const [rewards, setRewards] = useState({
    lp_rewards: 0,
    staking_rewards: 0,
    referral_rewards: 0,
    platform_incentives: 0
  });
  const [claimHistory, setClaimHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    if (token) {
      fetchRewards();
      fetchClaimHistory();
    }
  }, [token]);

  const fetchRewards = async () => {
    try {
      const response = await fetch(`${API}/rewards/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRewards(data.rewards || {});
      }
    } catch (error) {
      console.error('Error fetching rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClaimHistory = async () => {
    try {
      const response = await fetch(`${API}/rewards/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClaimHistory(data.claims || []);
      }
    } catch (error) {
      console.error('Error fetching claim history:', error);
    }
  };

  const handleClaim = async (rewardType) => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    const amount = rewards[rewardType];
    if (amount <= 0) {
      toast.error('No rewards to claim');
      return;
    }

    setClaiming(rewardType);
    try {
      const response = await fetch(`${API}/rewards/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reward_type: rewardType,
          wallet_address: account
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Successfully claimed ${amount.toLocaleString()} BETH!`);
        setRewards({...rewards, [rewardType]: 0});
        fetchClaimHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to claim rewards');
      }
    } catch (error) {
      toast.error('Error claiming rewards');
    } finally {
      setClaiming(null);
    }
  };

  const handleClaimAll = async () => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }

    const totalRewards = Object.values(rewards).reduce((a, b) => a + b, 0);
    if (totalRewards <= 0) {
      toast.error('No rewards to claim');
      return;
    }

    setClaiming('all');
    try {
      const response = await fetch(`${API}/rewards/claim-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ wallet_address: account })
      });
      
      if (response.ok) {
        toast.success(`Successfully claimed ${totalRewards.toLocaleString()} BETH!`);
        setRewards({
          lp_rewards: 0,
          staking_rewards: 0,
          referral_rewards: 0,
          platform_incentives: 0
        });
        fetchClaimHistory();
      } else {
        toast.error('Failed to claim rewards');
      }
    } catch (error) {
      toast.error('Error claiming rewards');
    } finally {
      setClaiming(null);
    }
  };

  const totalRewards = Object.values(rewards).reduce((a, b) => a + b, 0);

  const getColorClasses = (color) => {
    const colors = {
      pink: 'bg-pink-900/30 border-pink-500/30 text-pink-400',
      blue: 'bg-blue-900/30 border-blue-500/30 text-blue-400',
      green: 'bg-green-900/30 border-green-500/30 text-green-400',
      yellow: 'bg-yellow-900/30 border-yellow-500/30 text-yellow-400'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="claim-rewards">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift className="w-6 h-6 text-[#b8860b]" />
            Claim Rewards
          </h1>
          <p className="text-gray-400 text-sm">Claim your earned BETH rewards</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Total Rewards Card */}
        <Card className="bg-gradient-to-br from-[#b8860b]/20 to-[#b8860b]/5 border-[#b8860b]/30">
          <CardContent className="py-8">
            <div className="text-center">
              <Award className="w-12 h-12 text-[#b8860b] mx-auto mb-4" />
              <p className="text-gray-400 text-sm mb-2">Total Available Rewards</p>
              <p className="text-4xl font-bold text-white mb-1">
                {loading ? '...' : totalRewards.toLocaleString()} BETH
              </p>
              <p className="text-sm text-gray-500 mb-6">≈ ${totalRewards.toLocaleString()} USD</p>
              
              <Button 
                size="lg"
                className="bg-[#b8860b] hover:bg-[#9a7209]"
                onClick={handleClaimAll}
                disabled={totalRewards <= 0 || claiming === 'all'}
              >
                {claiming === 'all' ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Claim All Rewards
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Individual Reward Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REWARD_TYPES.map(type => {
            const Icon = type.icon;
            const amount = rewards[type.id] || 0;
            
            return (
              <Card key={type.id} className={`border ${getColorClasses(type.color)}`}>
                <CardContent className="py-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getColorClasses(type.color)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{type.name}</p>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-white">{amount.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">BETH available</p>
                    </div>
                    <Button
                      size="sm"
                      variant={amount > 0 ? 'default' : 'outline'}
                      className={amount > 0 ? 'bg-[#b8860b] hover:bg-[#9a7209]' : ''}
                      onClick={() => handleClaim(type.id)}
                      disabled={amount <= 0 || claiming === type.id}
                    >
                      {claiming === type.id ? 'Claiming...' : 'Claim'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* How to Earn More */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              How to Earn More BETH
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-white font-medium mb-1">Subscribe to Platform</p>
                <p className="text-xs text-gray-400 mb-2">Get BETH credits for platform access</p>
                <Link to="/subscribe">
                  <Button size="sm" variant="outline">Subscribe Now</Button>
                </Link>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-white font-medium mb-1">Stake BETH</p>
                <p className="text-xs text-gray-400 mb-2">Lock tokens to earn rewards</p>
                <Button size="sm" variant="outline" disabled>Coming Soon</Button>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-white font-medium mb-1">Refer Friends</p>
                <p className="text-xs text-gray-400 mb-2">Earn 100 BETH per referral</p>
                <Button size="sm" variant="outline" disabled>Coming Soon</Button>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-white font-medium mb-1">Use Platform</p>
                <p className="text-xs text-gray-400 mb-2">Deposits & credit draws earn bonus</p>
                <Link to="/credit">
                  <Button size="sm" variant="outline">Open Credit</Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Claim History */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Claim History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {claimHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No claims yet</p>
            ) : (
              <div className="space-y-2">
                {claimHistory.slice(0, 5).map((claim, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="text-white text-sm">{claim.reward_type?.replace('_', ' ')}</p>
                        <p className="text-xs text-gray-500">{new Date(claim.claimed_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <p className="text-green-400 font-medium">+{claim.amount?.toLocaleString()} BETH</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClaimRewards;
